import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { TIMEFRAME } from 'dateUtils';
import { getSortedOptionsFromStrings } from 'utils';
import { FilterOption } from 'models/Filters';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import {
  DashboardMetricColumns,
  DEAL_SORTING_VALUES,
  HeadingTranslations,
  MULTI_SORT_DIRECTION_COPY,
} from '../data';
import {
  DashboardMetric,
  LISTING_METRICS_PAYMENT_STATUS,
  METRIC_LISTING_STAGE,
  METRICS_LISTING_PAID_PACKAGE_LABEL,
} from '../types';
import useMultiSort from 'hooks/useMultiSort';

const HIDE_METRICS_FILTERS_PARAM = 'hideMetrics';

const BROKER_FILTER_KEY = 'brokerName';
const LISTING_FILTER_KEY = 'listingName';

const FILTER_COPY = {
  hideResolveManagedListings: 'isResolveManagedListing',
  hidePausedListings: 'isPausedListing',
  hideEndedListings: 'isEndedListing',
};

export default function useListingMetricsFilters() {
  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const getDashboardMetricsBrokerOptions = useCallback(
    async (searchValue: string) => {
      const {
        data: { brokers },
      } = await api.portfolio.admin.getBrokerSearchvalues(searchValue);

      return getSortedOptionsFromStrings(brokers);
    },
    [],
  );

  const getDashboardMetricsListingOptions = useCallback(
    async (searchValue: string) => {
      const {
        data: { listings },
      } = await api.portfolio.admin.getListingSearchvalues(searchValue);

      return getSortedOptionsFromStrings(listings);
    },
    [],
  );

  const getDashboardMetricsBrokerageFirmOptions = useCallback(
    async (searchValue: string) => {
      const {
        data: { brokerageFirms },
      } = await api.portfolio.admin.getBrokerageFirmSearchvalues(searchValue);

      return getSortedOptionsFromStrings(brokerageFirms);
    },
    [],
  );

  const hideMetricsFilterOption = useMemo<FilterOption>(
    () => ({
      value: HIDE_METRICS_FILTERS_PARAM,
      label: t('hideMetrics'),
      isMultiple: true,
      isSearchable: false,
      shouldSortOptions: false,
      options: createSelectOptions(FILTER_COPY),
    }),
    [createSelectOptions, t],
  );

  const brokerFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('broker'),
        BROKER_FILTER_KEY,
        BROKER_FILTER_KEY,
        getDashboardMetricsBrokerOptions,
      ) as FilterOption,
    [getDashboardMetricsBrokerOptions, getMultiSelectSearchableFilter, t],
  );

  const listingFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('listing'),
        LISTING_FILTER_KEY,
        LISTING_FILTER_KEY,
        getDashboardMetricsListingOptions,
      ) as FilterOption,
    [getDashboardMetricsListingOptions, getMultiSelectSearchableFilter, t],
  );

  const brokerageFirmFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('brokerageFirm'),
        BROKERAGE_FIRM_FILTER_KEY,
        BROKERAGE_FIRM_FILTER_KEY,
        getDashboardMetricsBrokerageFirmOptions,
      ) as FilterOption,
    [
      getDashboardMetricsBrokerageFirmOptions,
      getMultiSelectSearchableFilter,
      t,
    ],
  );

  const timeframeOptions = useMemo(
    () => createSelectOptions(Object.values(TIMEFRAME)),
    [createSelectOptions],
  );

  const booleanOptions = useMemo(
    () => [
      { value: 'true', label: t('yes') },
      { value: 'false', label: t('no') },
    ],
    [t],
  );

  const publishedOnFilterOption = useMemo<FilterOption>(
    () => ({
      value: TIMEFRAME_PARAM_KEYWORD,
      label: t('publishedDuring'),
      shouldSortOptions: false,
      options: timeframeOptions,
    }),
    [timeframeOptions, t],
  );

  const lastEditedFilterOption = useMemo<FilterOption>(
    () => ({
      value: LAST_EDITED_FILTER_KEY,
      label: t('lastEdited'),
      shouldSortOptions: false,
      options: timeframeOptions,
    }),
    [timeframeOptions, t],
  );

  const stageFilterOption = useMemo<FilterOption>(
    () => ({
      value: STAGE_FILTER_KEY,
      label: t('status'),
      isMultiple: true,
      options: createSelectOptions(Object.values(METRIC_LISTING_STAGE)),
    }),
    [createSelectOptions, t],
  );

  const paidPackageFilterOption = useMemo<FilterOption>(
    () => ({
      value: PAID_PACKAGE_FILTER_KEY,
      label: t('paidPackage'),
      isMultiple: true,
      options: Object.entries(METRICS_LISTING_PAID_PACKAGE_LABEL).map(
        ([value, label]) => ({ value, label: t(label) }),
      ),
    }),
    [t],
  );

  const paymentStatusFilterOption = useMemo<FilterOption>(
    () => ({
      value: PAYMENT_STATUS_FILTER_KEY,
      label: t('payment'),
      isMultiple: true,
      options: createSelectOptions(
        Object.values(LISTING_METRICS_PAYMENT_STATUS),
      ),
    }),
    [createSelectOptions, t],
  );

  const hasImFilterOption = useMemo<FilterOption>(
    () => ({
      value: HAS_IM_FILTER_KEY,
      label: t('imInDR'),
      shouldSortOptions: false,
      options: booleanOptions,
    }),
    [booleanOptions, t],
  );

  const hasNdaFilterOption = useMemo<FilterOption>(
    () => ({
      value: HAS_NDA_FILTER_KEY,
      label: t('ndaAttached'),
      shouldSortOptions: false,
      options: booleanOptions,
    }),
    [booleanOptions, t],
  );

  const filterOptions = useMemo(
    () => [
      hideMetricsFilterOption,
      brokerFilterOption,
      brokerageFirmFilterOption,
      listingFilterOption,
      stageFilterOption,
      paidPackageFilterOption,
      paymentStatusFilterOption,
      publishedOnFilterOption,
      lastEditedFilterOption,
      hasImFilterOption,
      hasNdaFilterOption,
    ],
    [
      hideMetricsFilterOption,
      brokerFilterOption,
      brokerageFirmFilterOption,
      listingFilterOption,
      stageFilterOption,
      paidPackageFilterOption,
      paymentStatusFilterOption,
      publishedOnFilterOption,
      lastEditedFilterOption,
      hasImFilterOption,
      hasNdaFilterOption,
    ],
  );

  const {
    multiSortValues: translatedSortOptions,
    multiSortDirectionTranslation,
  } = useMultiSort(
    DashboardMetricColumns,
    DEAL_SORTING_VALUES,
    HeadingTranslations,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(() => {
    const columnSortOptions = DashboardMetricColumns.reduce((acc, column) => {
      const sortValue = DEAL_SORTING_VALUES[column];

      if (MULTI_SORT_DIRECTION_COPY[sortValue])
        acc[column] = {
          sortDirectionTranslation: multiSortDirectionTranslation[sortValue],
        };

      return acc;
    }, {} as Partial<Record<keyof DashboardMetric, ColumnFilterOption>>);

    return {
      ...columnSortOptions,
      title: {
        ...columnSortOptions.title,
        options: listingFilterOption,
      },
      brokerName: {
        ...columnSortOptions.brokerName,
        options: brokerFilterOption,
      },
      brokerageFirm: {
        ...columnSortOptions.brokerageFirm,
        options: brokerageFirmFilterOption,
      },
      publishedOn: {
        ...columnSortOptions.publishedOn,
        options: publishedOnFilterOption,
      },
      lastEdited: {
        ...columnSortOptions.lastEdited,
        options: lastEditedFilterOption,
      },
      stage: {
        ...columnSortOptions.stage,
        options: stageFilterOption,
      },
      paidPackage: {
        ...columnSortOptions.paidPackage,
        options: paidPackageFilterOption,
      },
      paymentStatus: {
        ...columnSortOptions.paymentStatus,
        options: paymentStatusFilterOption,
      },
      hasIM: {
        ...columnSortOptions.hasIM,
        options: hasImFilterOption,
      },
      hasNDA: {
        ...columnSortOptions.hasNDA,
        options: hasNdaFilterOption,
      },
    } as Partial<Record<keyof DashboardMetric, ColumnFilterOption>>;
  }, [
    brokerFilterOption,
    brokerageFirmFilterOption,
    hasImFilterOption,
    hasNdaFilterOption,
    lastEditedFilterOption,
    listingFilterOption,
    multiSortDirectionTranslation,
    paidPackageFilterOption,
    paymentStatusFilterOption,
    publishedOnFilterOption,
    stageFilterOption,
  ]);

  return {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  };
}

const STAGE_FILTER_KEY = 'stage';
const PAID_PACKAGE_FILTER_KEY = 'paidPackage';
const PAYMENT_STATUS_FILTER_KEY = 'paymentStatus';
const HAS_IM_FILTER_KEY = 'hasIM';
const HAS_NDA_FILTER_KEY = 'hasNDA';
const LAST_EDITED_FILTER_KEY = 'lastEditedTimeframe';
const BROKERAGE_FIRM_FILTER_KEY = 'brokerageFirm';
